import angular from 'angular';

/**
 * The angularjs-color-picker library doesnt appear to be set up for the commonjs or es module
 * pattern as the main field in the package.json is pointing to the min file in its dist directory
 * which doesnt seem to be exporting anything. Because its not exporting the name, we have to manually
 * include the string name for our module dependency. Importing the module here so that
 * webpack will load the library in the bundle instead of having to put a <script> tag in the master
 * page to load it in.
 **/
import 'angularjs-color-picker/dist/angularjs-color-picker.js';
import 'angularjs-color-picker/dist/angularjs-color-picker.css';

import uiBootstrap from 'angular-ui-bootstrap';
import csmUtilTools from '../../util/tools/tools';

import csmColorPickerTemplate from './csmColorPickerDialog.template.html';
import './csmColorPicker.css';

export default angular
  .module('csm.ui.csmColorPicker', [uiBootstrap, csmUtilTools, 'color.picker'])

  /**
  * @ngdoc      component
  * @module     csm.ui.csmColorPicker
  * @name       csm.ui.csmColorPicker.component:csmColorPicker
  *
  * @param {object}    options     Optional options object for the color-picker directive. For a complete list visit: 
  *                                https://www.npmjs.com/package/angularjs-color-picker  
  * @param {string}    pickerLabel Optional. Label to be displayed at the top of the color picker modal.
  * @param {attribute} liveUpdate  Optional. When entered in the HTML it will update the model
  *                                as colors change in the modal. 
  *
  * @description
  * This is a wrapper for the color-picker directive. This will create an element and set the background
  * color. When the element is clicked on, a modal dialog will be displayed where the color can be changed
  * utilizing the color-picker directive. The ngModel can be updated as changes are made when liveUpdate
  * is present, otherwise the model is only updated on confirmation from the dialog.  
  * 
  * Note: Example not functioning
  * @example
    <example module="csmColorPickerExample">
      <file name="index.html">
        <div ng-controller="csmColorPickerController">
          <csm-color-picker ng-model="myColor"></csm-color-picker>
        </div>
      </file>
      <file name="script.js">
        angular.module('csmColorPickerExample', [ 'csm.ui.csmColorPicker' ]).
        controller("csmColorPickerController", function ($scope) 
          {
          $scope.myColor = "red";
          });
      </file>
    </example>
  *****************************************************************************/
  .component('csmColorPicker', {
    bindings: { options: '=', pickerLabel: '@', liveUpdate: '@' },
    require: { ngModel: 'ngModel' },
    template: '<div ng-click="$ctrl.onClick()" space-click tabindex=0></div>',
    controller: CSMColorPickerController
  }).name;

/* @ngInject */
function CSMColorPickerController($element, $uibModal, Tools) {
  /** Self Reference. */
  var mThis = this;

  /** Control div reference. */
  var mBGDiv = $element.find('div');

  /** Current parsed color string. */
  var mColor = null;

  /** Boolean indicator that the model value coming in was a number (Unsigned int) */
  var mUseNumeric = false;

  /** Indication to update the model when changing the color value. This is true
        when the liveUpdate attribute is present. */
  var mDoLiveUpdate = mThis.liveUpdate != undefined;

  /*************************************************************************
   * onClick
   *
   * Click handler to open a modal that will display the colorPicker directive.
   *************************************************************************/
  mThis.onClick = function () {
    /** Current color value before modal open */ var currentColor = mColor;

    $uibModal
      .open({
        animation: true,
        backdrop: false,
        windowTopClass: 'csmColorPickerDialog',
        template: csmColorPickerTemplate,
        resolve: {
          options: function () {
            return mThis.options;
          },
          color: function () {
            return mColor;
          },
          title: function () {
            return mThis.pickerLabel;
          },

          /*--------------------------------------------------*/
          /* If the live update is set, we need to update     */
          /* the color and the model as the color is changed. */
          /*--------------------------------------------------*/
          updateColor: function () {
            return mDoLiveUpdate ? updateColor : angular.noop;
          }
        },
        size: 'sm',
        controller: DialogController

        /*----------------------------------------------------------*/
        /* Update the color on resolve, otherwise reset to original.*/
        /*----------------------------------------------------------*/
      })
      .result.then(updateColor, function () {
        updateColor(currentColor);
      });
  };

  /***************************************************************************
   * $onInit
   *
   * Configure ngModel controller to work with this component.
   ***************************************************************************/
  mThis.$onInit = function () {
    /*--------------------------------------------------------------------*/
    /* Convert input model to valid color. Set mUseNumeric indicator and  */
    /* update control div color. When input is invalid, default to white. */
    /*--------------------------------------------------------------------*/
    mThis.ngModel.$formatters.push(function (value) {
      var color = '#FFFFFF';
      mUseNumeric = false;

      /*-------------------------------------------------------------*/
      /* Convert number values to hex values for display in the DOM. */
      /*-------------------------------------------------------------*/
      if (typeof value == 'number') {
        color = Tools.toHex(value);
        mUseNumeric = true;
      } else if (typeof value == 'string') {
        /*------------------------------------------------------*/
        /* Convert string values to hex format using tinycolor. */
        /*------------------------------------------------------*/
        // tinycolor expected to exist in global scope - loaded in by angularjs-color-picker
        var tc = tinycolor(value);
        if (tc.isValid()) color = tc.toHexString();
      }

      /*------------------------------*/
      /* Update the background color. */
      /*------------------------------*/
      setBackground(color);

      return color;
    });
  };

  /***************************************************************************
   * setBackground
   *
   * Sets the color of the background element.
   *
   * @param color  string value of the color
   ***************************************************************************/
  function setBackground(color) {
    /*-----------------------------------------*/
    /* Update the resolved color in the modal. */
    /*-----------------------------------------*/
    mColor = color;

    /*--------------------------------------*/
    /* Set the background color on the div. */
    /*--------------------------------------*/
    mBGDiv.css('background-color', color);
  }

  /***************************************************************************
   * setModel
   *
   * Sets the model to the passed color
   *
   * @param color  string value of the color
   ***************************************************************************/
  function setModel(color) {
    /*----------------------------------------*/
    /* If the model coming in is numeric,     */
    /* ensure the value being set is the same */
    /*----------------------------------------*/
    mThis.ngModel.$setViewValue(
      mUseNumeric ? parseInt(color.substr(1), 16) : color
    );
  }

  /***************************************************************************
   * updateColor
   *
   * Calls the setBackground and setModel functions.
   *
   * @param color  string value of the color
   ***************************************************************************/
  function updateColor(color) {
    setBackground(color);
    setModel(color);
  }
}

/** Default colorPicker options for the modal. */
var DEFAULT_OPTIONS = {
  required: false,
  format: 'hexstring',
  case: 'lower', // Sets the text for the format to lowercase.
  round: false, // Makes the gradient a rectangle
  horizontal: false, // Vertical Sliders
  dynamicHue: false, //Prevent hue shading
  /*--------------------*/
  /* Hide other sliders */
  /*--------------------*/
  saturation: false,
  lightness: false, // Note: In the square mode this is HSV and in round mode this is HSL
  alpha: false
};

/** Required colorPicker options for working within the dialog. */
var REQUIRED_OPTIONS = {
  inputClass: 'form-control',
  swatch: true, // Display swatch in input
  swatchBootstrap: true, // Adds/removes padding around the swatch buttons.
  swatchOnly: false, // Show the input field.
  inline: true, //Color picker inline
  hue: true
};

/*****************************************************************************
 * DialogController
 *
 * Modal dialog controller that will work with the colorPicker directive.
 *
 * @param $scope             Scope
 * @param $uibModalInstance  Modal instance
 * @param UserAgent          User agent service to determine the browser
 * @param options            Optional - options for the colorPicker directive can be
 *                           changed or overwritten
 * @param color              Current color value
 * @param title              Title to be displayed in the modal. Defaults to 'Choose a Color'
 * @param updateColor        Function to call when the color has been changed
 *****************************************************************************/
/* @ngInject */
function DialogController(
  $scope,
  $uibModalInstance,
  UserAgent,
  options,
  color,
  title,
  updateColor
) {
  /*-------------------------------------------*/
  /* If the browser is IE9, we need to add     */
  /* the gradient image via css to the slider. */
  /*-------------------------------------------*/
  $scope.ie9Class = UserAgent.isIE9() ? 'ie9Mode' : '';
  $scope.title = title || 'Choose a Color';

  /*----------------------------------*/
  /* colorPicker directive properties */
  /*----------------------------------*/
  /** colorPicker directive api object. The this context will refer to the ColorPicker controller instance.
        On a change we need the colorPicker directive to update its model as it does not happen when manually
        changing the color with the input. */
  $scope.api = {
    onChange: function (api, color) {
      this.ngModel = this.internalNgModel;
    }
  };

  /** ColorPicker ngModel reference. */
  $scope.color = color;

  /** ColorPicker options object. */
  $scope.options = angular.extend(
    {},
    DEFAULT_OPTIONS,
    options,
    REQUIRED_OPTIONS
  );

  /*------------------------------------------------------------*/
  /* Watch the color and update as the color selection changes. */
  /*------------------------------------------------------------*/
  $scope.$watch('color', updateColor);
}
